import { 
    collection, 
    doc, 
    getDoc, 
    getDocs, 
    query, 
    where, 
    orderBy,
    onSnapshot
  } from 'firebase/firestore';
  import { db } from './config';
  
  // ============================================================================
  // READ OPERATIONS
  // ============================================================================
  
  /**
   * Get single delivery by ID
   */
  export const getDelivery = async (deliveryId) => {
    try {
      const deliveryRef = doc(db, 'deliveries', deliveryId);
      const deliveryDoc = await getDoc(deliveryRef);
      
      if (deliveryDoc.exists()) {
        return { id: deliveryDoc.id, ...deliveryDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting delivery:', error);
      throw error;
    }
  };
  
  /**
   * Get single completed delivery by ID
   */
  export const getCompletedDelivery = async (deliveryId) => {
    try {
      const deliveryRef = doc(db, 'completed-deliveries', deliveryId);
      const deliveryDoc = await getDoc(deliveryRef);
      
      if (deliveryDoc.exists()) {
        return { id: deliveryDoc.id, ...deliveryDoc.data() };
      }
      return null;
    } catch (error) {
      console.error('Error getting completed delivery:', error);
      throw error;
    }
  };
  
  /**
   * Get available deliveries (state = 'active' and no courier)
   */
  export const getAvailableDeliveries = async (postalCode = null) => {
    try {
      let q = query(
        collection(db, 'deliveries'),
        where('state', '==', 'active'),
        where('courierId', '==', null),
        orderBy('createdAt', 'desc')
      );
  
      const snapshot = await getDocs(q);
      let deliveries = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // Filter by postal code client-side if provided
      if (postalCode) {
        deliveries = deliveries.filter(d => 
          d.from?.postalCode === postalCode || 
          d.to?.postalCode === postalCode
        );
      }
      
      return deliveries;
    } catch (error) {
      console.error('Error getting available deliveries:', error);
      throw error;
    }
  };
  
  /**
   * Get completed deliveries from completed-deliveries collection
   */
  export const getCompletedDeliveries = async (courierId) => {
    try {
      const q = query(
        collection(db, 'completed-deliveries'),
        where('courierId', '==', courierId),
        orderBy('completedAt', 'desc')
      );
  
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
      console.error('Error getting completed deliveries:', error);
      throw error;
    }
  };
  
  // ============================================================================
  // REAL-TIME LISTENERS
  // ============================================================================
  
  /**
   * Subscribe to all active deliveries in the system
   * Used to listen to all deliveries collection
   */
  export const subscribeToDeliveries = (callback, errorCallback) => {
    try {
      const q = query(
        collection(db, 'deliveries')
      );
  
      return onSnapshot(
        q,
        (snapshot) => {
          const deliveries = snapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
          }));
          callback(deliveries);
        },
        (error) => {
          console.error('Error in deliveries subscription:', error);
          if (errorCallback) errorCallback(error);
        }
      );
    } catch (error) {
      console.error('Error setting up deliveries subscription:', error);
      throw error;
    }
  };
  
  /**
   * Subscribe to single delivery
   */
  export const subscribeToDelivery = (deliveryId, callback, errorCallback) => {
    try {
      const deliveryRef = doc(db, 'deliveries', deliveryId);
      
      return onSnapshot(
        deliveryRef,
        (docSnapshot) => {
          if (docSnapshot.exists()) {
            callback({ id: docSnapshot.id, ...docSnapshot.data() });
          } else {
            // If not in active deliveries, check completed
            const completedRef = doc(db, 'completed-deliveries', deliveryId);
            return onSnapshot(
              completedRef,
              (completedSnapshot) => {
                if (completedSnapshot.exists()) {
                  callback({ id: completedSnapshot.id, ...completedSnapshot.data(), completed: true });
                } else {
                  callback(null);
                }
              }
            );
          }
        },
        (error) => {
          console.error('Error in delivery subscription:', error);
          if (errorCallback) errorCallback(error);
        }
      );
    } catch (error) {
      console.error('Error setting up delivery subscription:', error);
      throw error;
    }
  };
  
  
  